/**
 * Textos dos eventos (§13.5, dono único em todas as formas): `eventText(ev, view, form)` e `eventRich(…)`.
 * O texto sai SEMPRE dos dados (`ElectionEvent.data`) pelos modelos de strings.ts (`event.*`, `pin.*`); o campo `text`
 * do fio é ignorado (vocabulário fechado e simetria, §13.7). Hora exibida = a de `occurred_at` (§13.5), em Brasília,
 * nunca a hora em que o cliente viu o evento (AC-13.5-04).
 *
 * Folha (FE §4.2): `copy/` não importa `contracts/` nem `model/`, nem em tipo. Os tipos abaixo são estruturais e
 * aceitam `ElectionEvent` (contracts/events.ts), `ResultsView` (model/types.ts) e `Geography` (contracts/static.ts)
 * sem conversão; tests/unit/copy.events.test.ts confere a compatibilidade.
 *
 * Formas (§13.5): `line` (item do UpdatesFeed) · `line2` (2ª linha do lote "+N seções", §7.15) · `short` (EventToast e
 * manchete do EventOverlay) · `sr` (LiveRegion e nome acessível do item) · `ticker` (TvTicker) · `pin` (TimelinePins) ·
 * `overlayKicker` / `overlaySub` (EventOverlay). Forma que o tipo não tem → `null`.
 */
import { fmtInt, fmtPct, fmtPoints, fmtSections, fmtVotesLong } from '../format/numbers';
import { joinList, PLACES, type PlaceKey, placeName, type RegionKey, regionIn, ufIn } from '../format/places';
import { fmtTimeAt } from '../format/time';
import { EDITORIAL } from './editorial';
import { type CopyKey, type Rich, richText, t, tRich } from './strings';

// ── Tipos estruturais (espelho de contracts/events.ts, model/types.ts e contracts/static.ts) ─────────────────
type Gender = 'F' | 'M';
type Pair<T> = readonly [T, T];

export interface EventCandidate {
  readonly number: string;
  readonly display_name: string;
  readonly gender: Gender | null;
}

export type EventChanceBand = 'open_race' | 'favorite' | 'very_likely' | 'above_99';

/** Espelho de `ElectionEventData` (§14.5). */
export type EventData =
  | { readonly type: 'results_open' }
  | { readonly type: 'abroad_first_votes'; readonly cities: readonly string[] }
  | { readonly type: 'uf_first_votes'; readonly places: readonly string[] }
  | {
      readonly type: 'sections_batch';
      readonly sections: number;
      readonly places: readonly string[];
      readonly shares: Pair<number>;
      readonly national_sections_fraction: number;
    }
  | { readonly type: 'count_milestone'; readonly milestone: number; readonly shares: Pair<number> }
  | {
      readonly type: 'national_lead_change' | 'uf_lead_change';
      readonly from: string;
      readonly to: string;
      readonly sections_fraction: number;
    }
  | {
      readonly type: 'abroad_half' | 'abroad_complete' | 'uf_complete' | 'capital_complete';
      readonly leader: string | null;
      readonly shares: Pair<number>;
      readonly margin_pp: number;
      readonly ibge?: string;
    }
  | { readonly type: 'math_decision' | 'tse_result'; readonly winner: string }
  | {
      readonly type: 'model_activated' | 'model_band_change';
      readonly favorite: string | null;
      readonly band: EventChanceBand;
    }
  | { readonly type: 'model_paused' | 'model_resumed' | 'model_ended'; readonly reason: string | null }
  | { readonly type: 'tse_stalled' | 'tse_resumed'; readonly since: string }
  | {
      readonly type: 'correction';
      readonly field: 'sections_counted' | 'valid_votes' | 'votes';
      readonly candidate?: string;
      readonly before: number;
      readonly after: number;
    }
  | {
      /** P-27: a mudança de estado do cartão de um lugar. Sem frase aqui: o cartão muda no painel (DR-33). */
      readonly type: 'insight_state';
      readonly place: { readonly level: string; readonly code?: string };
      readonly from: string | null;
      readonly to: string;
      readonly signals: readonly string[];
    };

/** Espelho do que os modelos leem de `ElectionEvent` (§14.5). */
export interface EventLike {
  readonly office: 'president' | 'governor';
  /** tse_ht do fato: a hora exibida (§13.5). */
  readonly occurred_at: string;
  readonly scope: { readonly level: string; readonly code?: string };
  readonly data: EventData;
}

interface EventPlace {
  readonly sectionsFraction: number;
  readonly shares: Pair<number>;
  readonly diffVotes: number;
}
interface EventRegion {
  readonly remainingElectorate: number;
  readonly leader: 0 | 1 | null;
}

/** O que os modelos leem de `ResultsView` (§14.8.11): nomes e, para o EventOverlay, o nacional e as regiões. */
export interface EventTextView {
  /** Presidente, [13, 22]. */
  readonly candidates: Pair<EventCandidate>;
  readonly national?: EventPlace;
  readonly regions?: { readonly [code: string]: EventRegion | undefined };
  readonly governor?: { readonly [uf: string]: { readonly candidates: Pair<EventCandidate> } | undefined };
}

/** O que os modelos leem de `Geography` (§14.2.1): nome da capital e das cidades do exterior. */
export interface EventTextGeo {
  readonly municipalities?: readonly { readonly ibge: string; readonly name: string; readonly uf: string }[];
  readonly abroad_cities?: readonly { readonly tse: string; readonly name: string }[];
}

export const EVENT_FORMS = ['line', 'line2', 'short', 'sr', 'ticker', 'pin', 'overlayKicker', 'overlaySub'] as const;
export type EventForm = (typeof EVENT_FORMS)[number];

/** Motivos públicos dos códigos de `MODEL_EVENT_REASONS` (P-04, contracts/probability.ts). */
const MODEL_REASON_KEYS: Readonly<Record<string, CopyKey>> = {
  fonte_sem_novidade: 'event.modelReason.sourceStalled',
  saude_do_modelo: 'event.modelReason.modelHealth',
  decisao_editorial: 'event.modelReason.editorial',
};

const BAND_KEYS: Readonly<Record<EventChanceBand, CopyKey>> = {
  open_race: 'event.modelBand.openRace',
  favorite: 'event.modelBand.favorite',
  very_likely: 'event.modelBand.veryLikely',
  above_99: 'event.modelBand.above99',
};

const FIELD_KEYS = {
  sections_counted: 'event.correction.field.sectionsCounted',
  valid_votes: 'event.correction.field.validVotes',
  votes: 'event.correction.field.votes',
} as const satisfies Record<string, CopyKey>;

const REGION_ORDER: readonly RegionKey[] = ['N', 'NE', 'CO', 'SE', 'S', 'EX'];

// ── Utilitários ─────────────────────────────────────────────────────────────────────────────────────────────

/** Hora exibida de um evento: `fmtTime(tMin(occurred_at))`, Brasília (§13.5). Igual em qualquer aparelho e no replay. */
export function eventHour(ev: Pick<EventLike, 'occurred_at'>): string {
  return fmtTimeAt(Date.parse(ev.occurred_at));
}

const asPlace = (code: string | undefined): PlaceKey | null =>
  code !== undefined && Object.hasOwn(PLACES, code) ? (code as PlaceKey) : null;

const fold = (s: string) => s.normalize('NFD').replace(/\p{M}/gu, '').toLocaleLowerCase('pt-BR');

type Vars = Record<string, string | number | Gender | null | undefined>;

/** Variáveis de um candidato: nome, gênero (concordância) e número (trecho rico, §7.15). */
function who(name: string, c: EventCandidate): Vars {
  return { [name]: c.display_name, [`${name}.gender`]: c.gender, [`${name}.number`]: c.number };
}

const indexOf = (pair: Pair<EventCandidate>, num: string | null | undefined): 0 | 1 | null =>
  num == null ? null : pair[0].number === num ? 0 : pair[1].number === num ? 1 : null;

/** {X}/{Y} e {pX}/{pY} a partir do índice do líder (fatias na ordem [A, B]). */
function xy(pair: Pair<EventCandidate>, x: 0 | 1, shares: Pair<number>): Vars {
  const y = x === 0 ? 1 : 0;
  return { ...who('X', pair[x]), ...who('Y', pair[y]), pX: fmtPct(shares[x], 1), pY: fmtPct(shares[y], 1) };
}

/** {A}/{B}: ordem fixa 13 | 22 (número menor primeiro), usada nos empates. */
const ab = (pair: Pair<EventCandidate>): Vars => ({ ...who('A', pair[0]), ...who('B', pair[1]) });

/** Colocação pelas fatias: o maior primeiro; empate exato fica na ordem 13 | 22. */
const placement = (shares: Pair<number>): 0 | 1 => (shares[1] > shares[0] ? 1 : 0);

const geoCache = new WeakMap<object, Map<string, unknown>>();
function lookup<T extends { name: string }>(
  list: readonly T[] | undefined,
  id: (item: T) => string,
  code: string,
): T | null {
  if (!list) return null;
  let map = geoCache.get(list) as Map<string, T> | undefined;
  if (!map) {
    map = new Map(list.map((item) => [id(item), item]));
    geoCache.set(list, map);
  }
  return map.get(code) ?? null;
}

/** Insere `inner` (texto rico) no lugar de `{faixa}`/`{campo}` do modelo externo, preservando as marcas. */
function splice(key: CopyKey, vars: Vars, slot: string, inner: Rich): Rich {
  const MARK = '\u0000';
  const out: Rich = [];
  const push = (part: Rich[number]) => {
    if (part.text === '') return;
    const last = out[out.length - 1];
    if (last && last.candidate === undefined && part.candidate === undefined) last.text += part.text;
    else out.push({ ...part });
  };
  for (const part of tRich(key, { ...vars, [slot]: MARK })) {
    const at = part.text.indexOf(MARK);
    if (at < 0) {
      push(part);
      continue;
    }
    push({ ...part, text: part.text.slice(0, at) });
    for (const p of inner) push(p);
    push({ ...part, text: part.text.slice(at + MARK.length) });
  }
  return out;
}

/** line, ticker e sr iguais (todos os tipos sem forma própria, §13.5.1–§13.5.4). */
function plain(form: EventForm, build: () => Rich | null): Rich | null {
  return form === 'line' || form === 'ticker' || form === 'sr' ? build() : null;
}

// ── Modelos por tipo ────────────────────────────────────────────────────────────────────────────────────────

function presidentDecision(
  d: { readonly type: 'math_decision' | 'tse_result'; readonly winner: string },
  view: EventTextView,
  form: EventForm,
  hour: string,
): Rich | null {
  const pair = view.candidates;
  const x = indexOf(pair, d.winner);
  if (x === null) return null;
  const X = who('X', pair[x]);
  const math = d.type === 'math_decision';
  // ORD-F-150 (item 1): com a proposta aprovada, fora do herói também nada de "é eleito" antes do TSE; depois do TSE,
  // "{X} é eleito presidente, segundo o TSE" (resposta à PERGUNTA-F-10, 20/10 08:51)
  const count = EDITORIAL[1] === 'proposal';
  switch (form) {
    case 'line':
    case 'ticker':
    case 'sr':
      if (math) return tRich(count ? 'event.mathDecision.line.count' : 'event.mathDecision.line', X);
      return tRich(count ? 'event.tseResult.line.count' : 'event.tseResult.line', X);
    case 'short':
      if (math) return tRich(count ? 'event.mathDecision.short.count' : 'event.mathDecision.short', X);
      return tRich(count ? 'event.tseResult.short.count' : 'event.tseResult.short', X);
    case 'pin':
      return tRich(math ? (count ? 'pin.math.count' : 'pin.math') : 'pin.tse', { HHhMM: hour });
    case 'overlayKicker':
      return tRich(math ? 'event.mathDecision.overlayKicker' : 'event.tseResult.overlayKicker', { HHhMM: hour });
    case 'overlaySub': {
      const n = view.national;
      if (!n) return null;
      const S = fmtSections(n.sectionsFraction);
      if (math) return tRich('event.mathDecision.overlaySub', { S });
      return tRich('event.tseResult.overlaySub', { ...X, S, pX: fmtPct(n.shares[x], 1) });
    }
    default:
      return null;
  }
}

function nationalLeadChange(
  d: { readonly from: string; readonly to: string; readonly sections_fraction: number },
  view: EventTextView,
  form: EventForm,
  hour: string,
): Rich | null {
  const pair = view.candidates;
  const x = indexOf(pair, d.to);
  const y = indexOf(pair, d.from);
  if (x === null || y === null || x === y) return null;
  const vars: Vars = { ...who('X', pair[x]), ...who('Y', pair[y]), S: fmtSections(d.sections_fraction) };
  switch (form) {
    case 'line':
    case 'ticker':
    case 'sr':
      return tRich('event.nationalLeadChange.line', vars);
    case 'short':
      return tRich('event.nationalLeadChange.short', vars);
    case 'pin':
      return tRich('pin.leadChange', { HHhMM: hour });
    case 'overlayKicker':
      return tRich('event.nationalLeadChange.overlayKicker', { HHhMM: hour });
    case 'overlaySub': {
      // §13.5.1: {S} do evento; {V} = vantagem do líder; {reg_em} = região com mais eleitores por apurar; {Z} = líder dela
      const n = view.national;
      if (!n) return null;
      const sub: Vars = {
        ...who('X', pair[x]),
        S: fmtSections(d.sections_fraction),
        V: fmtVotesLong(Math.abs(n.diffVotes)),
      };
      let best: RegionKey | null = null;
      let most = 0;
      for (const code of REGION_ORDER) {
        const r = view.regions?.[code];
        if (r && r.remainingElectorate > most) {
          most = r.remainingElectorate;
          best = code;
        }
      }
      const z = best === null ? null : (view.regions?.[best]?.leader ?? null);
      if (best === null || z === null) return tRich('event.nationalLeadChange.overlaySub.noRegion', sub);
      const reg = { reg_em: regionIn(best) };
      if (z === x) return tRich('event.nationalLeadChange.overlaySub.same', { ...sub, ...reg });
      return tRich('event.nationalLeadChange.overlaySub', { ...sub, ...reg, ...who('Z', pair[z]) });
    }
    default:
      return null;
  }
}

function build(ev: EventLike, view: EventTextView, form: EventForm, geo: EventTextGeo | null): Rich | null {
  const d = ev.data;
  const pair = view.candidates;
  const hour = eventHour(ev);
  const scopePlace = asPlace(ev.scope.code);
  switch (d.type) {
    case 'results_open':
      return plain(form, () => tRich('event.resultsOpen.line'));

    case 'abroad_first_votes':
      return plain(form, () => {
        const names = d.cities
          .map((tse) => lookup(geo?.abroad_cities, (c) => c.tse, tse)?.name)
          .filter((name): name is string => typeof name === 'string' && name !== '');
        if (names.length === 0) return tRich('event.abroadFirstVotes.line.none');
        if (names.length === 1) return tRich('event.abroadFirstVotes.line.one', { C1: names[0] });
        return tRich('event.abroadFirstVotes.line.many', { LISTA: joinList(names) });
      });

    case 'abroad_half':
    case 'abroad_complete': {
      const half = d.type === 'abroad_half';
      return plain(form, () => {
        const x = indexOf(pair, d.leader);
        if (x === null) return tRich(half ? 'event.abroadHalf.line.tie' : 'event.abroadComplete.line.tie', ab(pair));
        return tRich(half ? 'event.abroadHalf.line' : 'event.abroadComplete.line', xy(pair, x, d.shares));
      });
    }

    case 'uf_first_votes':
      return plain(form, () => {
        const ufs = d.places.map(asPlace).filter((p): p is PlaceKey => p !== null);
        if (ufs.length === 0) return null;
        if (ufs.length > 3) return tRich('event.ufFirstVotes.line.many', { N: ufs.length });
        return tRich('event.ufFirstVotes.line.list', { LISTA: joinList(ufs.map(ufIn)) });
      });

    case 'uf_complete':
      return plain(form, () => {
        if (scopePlace === null) return null;
        const UF = placeName(scopePlace);
        const x = indexOf(pair, d.leader);
        if (x === null) return tRich('event.ufComplete.line.tie', { UF, ...ab(pair) });
        return tRich('event.ufComplete.line', { UF, ...xy(pair, x, d.shares) });
      });

    case 'capital_complete':
      return plain(form, () => {
        const ibge = d.ibge ?? ev.scope.code;
        const mun = ibge === undefined ? null : lookup(geo?.municipalities, (m) => m.ibge, ibge);
        const uf = asPlace(mun?.uf);
        if (!mun || uf === null) return null;
        const UF = placeName(uf);
        // Capital com o nome da UF (SP, RJ): "{UF}: capital apurada, …" (§13.5.1)
        const sameName = fold(mun.name) === fold(UF);
        const x = indexOf(pair, d.leader);
        if (x === null) {
          const key = sameName ? 'event.capitalComplete.line.sameNameTie' : 'event.capitalComplete.line.tie';
          return tRich(key, { UF, CAP: mun.name, ...ab(pair) });
        }
        const pontos = fmtPoints(d.margin_pp / 100, 1);
        const vars = { UF, CAP: mun.name, pontos, ...who('X', pair[x]) };
        return tRich(sameName ? 'event.capitalComplete.line.sameName' : 'event.capitalComplete.line', vars);
      });

    case 'sections_batch': {
      const x = placement(d.shares);
      const vars: Vars = { N: d.sections, ...xy(pair, x, d.shares) };
      // {SIGLAS}: siglas em ordem de Δ, até 4 (3 + "e mais N"), o Exterior por extenso (§13.5.1)
      const siglas = d.places.map((p) => (p === 'ZZ' ? placeName('ZZ') : p));
      const SIGLAS = joinList(siglas, { max: 4 });
      switch (form) {
        case 'line':
          return tRich('event.sectionsBatch.line', vars);
        case 'line2':
          return tRich('event.sectionsBatch.line2', vars);
        case 'ticker':
          return siglas.length > 0
            ? tRich('event.sectionsBatch.ticker', { ...vars, SIGLAS })
            : tRich('event.sectionsBatch.ticker.noPlaces', vars);
        case 'sr':
          return siglas.length > 0
            ? tRich('event.sectionsBatch.sr', { ...vars, SIGLAS })
            : tRich('event.sectionsBatch.sr.noPlaces', vars);
        default:
          return null;
      }
    }

    case 'count_milestone':
      return plain(form, () =>
        tRich('event.countMilestone.line', {
          M: Math.round(d.milestone * 100),
          ...xy(pair, placement(d.shares), d.shares),
        }),
      );

    case 'national_lead_change':
      return nationalLeadChange(d, view, form, hour);

    case 'uf_lead_change':
      return plain(form, () => {
        const x = indexOf(pair, d.to);
        const y = indexOf(pair, d.from);
        if (scopePlace === null || x === null || y === null || x === y) return null;
        return tRich('event.ufLeadChange.line', {
          UF: placeName(scopePlace),
          ...who('X', pair[x]),
          ...who('Y', pair[y]),
        });
      });

    case 'math_decision':
    case 'tse_result': {
      if (ev.office === 'president') return presidentDecision(d, view, form, hour);
      // §13.5.2: governador, escopo UF, saliência 2 (sem short, pino nem overlay)
      return plain(form, () => {
        if (scopePlace === null) return null;
        const gov = view.governor?.[scopePlace]?.candidates;
        const x = gov ? indexOf(gov, d.winner) : null;
        if (!gov || x === null) return null;
        // ORD-F-150 (item 1): a vantagem antes do TSE; depois, "…, segundo o TSE" (PERGUNTA-F-10, 20/10 08:51)
        const count = EDITORIAL[1] === 'proposal';
        const mathKey = count ? 'event.governorMathDecision.line.count' : 'event.governorMathDecision.line';
        const tseKey = count ? 'event.governorTseResult.line.count' : 'event.governorTseResult.line';
        const key = d.type === 'math_decision' ? mathKey : tseKey;
        return tRich(key, { UF: placeName(scopePlace), ...who('X', gov[x]) });
      });
    }

    case 'model_activated':
    case 'model_band_change':
      return plain(form, () => {
        // {X} = data.favorite (o favorito da projeção, não o líder da contagem, §13.5.3)
        const x = indexOf(pair, d.favorite);
        const faixa =
          d.band === 'open_race' || x === null
            ? tRich(BAND_KEYS.open_race)
            : tRich(BAND_KEYS[d.band], who('X', pair[x]));
        const key = d.type === 'model_activated' ? 'event.modelActivated.line' : 'event.modelBandChange.line';
        return splice(key, { HHhMM: hour }, 'faixa', faixa);
      });

    case 'model_paused':
      return plain(form, () => {
        const reasonKey = d.reason === null ? undefined : MODEL_REASON_KEYS[d.reason];
        if (!reasonKey) return tRich('event.modelPaused.line', { HHhMM: hour });
        return tRich('event.modelPaused.line.reason', { HHhMM: hour, motivo: t(reasonKey) });
      });
    case 'model_resumed':
      return plain(form, () => tRich('event.modelResumed.line', { HHhMM: hour }));
    case 'model_ended':
      return plain(form, () => tRich('event.modelEnded.line', { HHhMM: hour }));

    case 'tse_stalled':
      // {HHhMM} = data.since (§13.5.4)
      return plain(form, () => tRich('event.tseStalled.line', { HHhMM: fmtTimeAt(Date.parse(d.since)) }));
    case 'tse_resumed':
      return plain(form, () => tRich('event.tseResumed.line', { HHhMM: hour }));

    case 'correction':
      return plain(form, () => {
        // {UF} = "Brasil" no escopo nacional (§13.5.4)
        const UF = ev.scope.level === 'BR' || scopePlace === null ? placeName('BR') : placeName(scopePlace);
        const vars: Vars = { UF, antes: fmtInt(d.before), depois: fmtInt(d.after) };
        if (d.field !== 'votes') return splice('event.correction.line', vars, 'campo', tRich(FIELD_KEYS[d.field]));
        const people = ev.office === 'governor' && scopePlace ? view.governor?.[scopePlace]?.candidates : pair;
        const c = people ? indexOf(people, d.candidate) : null;
        if (!people || c === null) return null;
        return splice('event.correction.line', vars, 'campo', tRich(FIELD_KEYS.votes, who('NOME', people[c])));
      });

    // P-27 (DR-33): nenhuma forma de texto. Sem frase, o evento não entra na lista de atualizações, no toast, no
    // overlay, no modo TV nem na LiveRegion; o painel de insights (FA-12) lê o cartão do índice, e a linha do tempo
    // põe o pino da entrada e o do desfecho.
    case 'insight_state':
      return null;
  }
}

/**
 * Texto rico do evento na forma pedida (§13.5): nomes de candidato marcados (`candidate`), para `<b>` na cor do
 * partido. `null` quando o tipo não tem essa forma ou falta o dado do nome (candidato, capital, UF).
 * `geo` dá o nome da capital (`capital_complete`) e das cidades do exterior (`abroad_first_votes`); sem ele, a capital
 * não tem texto e o Exterior usa a frase sem cidades.
 */
export function eventRich(
  ev: EventLike,
  view: EventTextView,
  form: EventForm,
  geo: EventTextGeo | null = null,
): Rich | null {
  return build(ev, view, form, geo);
}

/** Texto puro do evento na forma pedida (§13.5). `null` quando a forma não existe para o tipo. */
export function eventText(
  ev: EventLike,
  view: EventTextView,
  form: EventForm,
  geo: EventTextGeo | null = null,
): string | null {
  const rich = build(ev, view, form, geo);
  return rich === null ? null : richText(rich);
}
